import React from "react";
import { Modal } from "react-bootstrap";
import AutocompleteInput from "../sub/AutocompleteInput";
import EqState from "../../enums/EqState";
import "../../css/rentals/rentals.css";

class RentalValidationModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      refInterneFocused: false,
    };

    // Refs pour les AutocompleteInput
    this.typeInputRef = React.createRef();
    this.brandInputRef = React.createRef();
    this.modelInputRef = React.createRef();
  }

  // Déterminer si l'équipement est libre (existe mais non assigné à un établissement)
  isEquipmentFree = () => {
    const { selectedEquipment, rental } = this.props;
    if (!selectedEquipment) return false;

    // L'équipement est libre s'il n'a pas d'establishment_id
    // OU si l'establishment_id correspond déjà à l'établissement de la location
    if (!selectedEquipment.establishment_id) return true;
    if (rental?.establishment &&
        selectedEquipment.establishment_id.toString() === rental.establishment._id.toString()) {
      return true;
    }
    return false;
  };

  handleRefInterneFocus = () => {
    this.setState({ refInterneFocused: true });
  };

  handleRefInterneBlur = () => {
    setTimeout(() => {
      this.setState({ refInterneFocused: false });
    }, 200);
  };

  // Vérifier si le champ Marque a une valeur (sélectionnée ou saisie)
  hasBrandValue = () => {
    const { selectedBrand } = this.props;
    if (selectedBrand) return true;
    const brandInputValue = this.brandInputRef.current?.getInputValue()?.trim();
    return !!brandInputValue;
  };

  // Force un re-render pour mettre à jour l'état disabled du champ Modèle
  handleBrandInputChange = () => {
    this.forceUpdate();
  };

  // Gestion de la validation avec auto-création des éléments manquants
  handleConfirmWithAutoCreate = async () => {
    const {
      selectedMaterialType,
      selectedBrand,
      selectedModel,
      onConfirm,
      selectedEquipment,
    } = this.props;

    // Si un équipement existant est sélectionné, pas besoin de créer Type/Marque/Modèle
    if (selectedEquipment) {
      onConfirm();
      return;
    }

    // Création séquentielle si nécessaire : Type -> Brand -> Model
    const createIfNeeded = async () => {
      return new Promise((resolve) => {
        // Étape 1: Créer le type si nécessaire
        if (!selectedMaterialType && this.typeInputRef.current) {
          this.typeInputRef.current.triggerCreate(() => {
            // Étape 2: Créer la marque si nécessaire
            if (!selectedBrand && this.brandInputRef.current) {
              this.brandInputRef.current.triggerCreate(() => {
                // Étape 3: Créer le modèle si nécessaire
                if (!selectedModel && this.modelInputRef.current) {
                  this.modelInputRef.current.triggerCreate(() => {
                    resolve();
                  });
                } else {
                  resolve();
                }
              });
            } else if (!selectedModel && this.modelInputRef.current) {
              this.modelInputRef.current.triggerCreate(() => {
                resolve();
              });
            } else {
              resolve();
            }
          });
        } else if (!selectedBrand && this.brandInputRef.current) {
          this.brandInputRef.current.triggerCreate(() => {
            if (!selectedModel && this.modelInputRef.current) {
              this.modelInputRef.current.triggerCreate(() => {
                resolve();
              });
            } else {
              resolve();
            }
          });
        } else if (!selectedModel && this.modelInputRef.current) {
          this.modelInputRef.current.triggerCreate(() => {
            resolve();
          });
        } else {
          resolve();
        }
      });
    };

    await createIfNeeded();

    // Petit délai pour laisser le state se propager
    setTimeout(() => {
      onConfirm();
    }, 100);
  };

  // Vérifier si l'équipement a une location active COHERENTE
  // On bloque seulement si l'établissement de l'équipement correspond à celui de la location active
  // Sinon c'est une incohérence de données qu'on nettoiera automatiquement
  hasActiveRental = () => {
    const { selectedEquipment } = this.props;
    if (!selectedEquipment) return false;
    if (!selectedEquipment.establishment_id) return false;
    if (!selectedEquipment.has_active_rental) return false;

    // Vérifier que l'établissement de l'équipement correspond à celui de la location active
    const equipmentEstablishmentId = selectedEquipment.establishment_id?.toString() || selectedEquipment.establishment_id;
    const rentalEstablishmentId = selectedEquipment.active_rental?.rental_establishment?._id?.toString() ||
                                   selectedEquipment.active_rental?.rental_establishment?._id;

    // Bloquer seulement si les établissements correspondent
    return equipmentEstablishmentId && rentalEstablishmentId && equipmentEstablishmentId === rentalEstablishmentId;
  };

  // Vérifier si le formulaire est valide (en prenant en compte les valeurs saisies non sélectionnées)
  isFormValidWithPendingValues = () => {
    const {
      validationRefInterne,
      selectedEquipmentState,
      selectedEquipment,
      selectedMaterialType,
      selectedBrand,
      selectedModel,
      confirmReassignment,
      rental,
    } = this.props;

    // Validation de base
    if (!validationRefInterne || validationRefInterne.trim() === "") return false;
    if (!selectedEquipmentState) return false;

    // Bloquer si l'équipement a un établissement assigné ET une location active
    if (this.hasActiveRental()) return false;

    // Déterminer le scénario
    let scenario = null;
    if (selectedEquipment) {
      if (
        selectedEquipment.establishment_id &&
        rental?.establishment &&
        selectedEquipment.establishment_id.toString() !== rental.establishment._id.toString()
      ) {
        scenario = "existing-assigned";
      } else {
        scenario = "existing-free";
      }
    } else {
      scenario = "new";
    }

    // Scénario équipement existant libre : vérifier la date de désinfection si l'équipement n'a pas d'établissement
    if (scenario === "existing-free") {
      // Si l'équipement est libre (pas d'establishment_id), la date de désinfection est requise
      if (this.isEquipmentFree() && !this.props.disinfectionDate) {
        return false;
      }
      return true;
    }

    // Scénario équipement existant assigné ailleurs
    if (scenario === "existing-assigned") return confirmReassignment;

    // Scénario nouveau équipement : vérifier que les champs ont une valeur (sélectionnée ou saisie)
    const hasType = selectedMaterialType || (this.typeInputRef.current?.getInputValue()?.trim());
    const hasBrand = selectedBrand || (this.brandInputRef.current?.getInputValue()?.trim());
    const hasModel = selectedModel || (this.modelInputRef.current?.getInputValue()?.trim());

    return hasType && hasBrand && hasModel;
  };

  render() {
    const {
      show,
      rental,
      selectedEquipment,
      selectedEquipmentState,
      selectedMaterialType,
      selectedBrand,
      selectedModel,
      validationRefInterne,
      isLoading,
      materialsTypes,
      brands,
      onClose,
      onEquipmentStateChange,
      onMaterialTypeChange,
      onCreateMaterialType,
      onBrandChange,
      onModelChange,
      onCreateBrand,
      onCreateModel,
      onRefInterneChange,
      onRefInterneSelect,
      getModelsByBrand,
      equipmentSearchResults,
      isSearchingEquipments,
      confirmReassignment,
    } = this.props;

    if (!show || !rental) return null;

    const productName = rental.product ? rental.product.designation : "Produit";
    const establishmentName = rental.establishment
      ? rental.establishment.name
      : "Établissement";

    // Déterminer le scénario basé sur l'équipement sélectionné
    let scenario = null;
    let warningMessage = null;

    if (selectedEquipment) {
      if (
        selectedEquipment.establishment_id &&
        rental.establishment &&
        selectedEquipment.establishment_id.toString() !==
          rental.establishment._id.toString()
      ) {
        scenario = "existing-assigned";
        const assignedClientName =
          selectedEquipment.establishment?.name || "un autre client";
        warningMessage = `Attention : cet équipement est actuellement assigné à ${assignedClientName}. En confirmant, vous allez réassigner cet équipement à ${establishmentName}.`;
      } else {
        scenario = "existing-free";
      }
    } else if (typeof validationRefInterne === "string" && validationRefInterne.trim() !== "") {
      scenario = "new";
    }

    // Afficher les champs Type, Marque, Modèle par défaut, sauf si un équipement existant est sélectionné
    const showExtraFields = scenario !== "existing-free" && scenario !== "existing-assigned";

    // Récupérer les modèles filtrés par marque
    const modelOptions = selectedBrand ? getModelsByBrand(selectedBrand) : [];

    return (
      <Modal
        show={show}
        onHide={onClose}
        backdrop={isLoading ? "static" : true}
        keyboard={!isLoading}
        centered
        size="md"
        className="rental-validation-modal"
        dialogClassName="modal-dialog-rounded modal-dialog-scrollable"
        scrollable
      >
        <Modal.Header className="rental-modal-header-gradient">
          <Modal.Title className="rental-modal-title">
            <i className="fas fa-check-circle"></i>
            Validation de location
          </Modal.Title>
          {!isLoading && (
            <button
              type="button"
              onClick={onClose}
              className="rental-validation-modal-close-btn"
            >
              <span>&times;</span>
            </button>
          )}
        </Modal.Header>

        <Modal.Body className="rental-modal-body">
          <div className="rental-modal-body-inner">
            <div className="rental-modal-product-section">
              <div className="rental-modal-product-label">
                Vous êtes sur le point de valider la location pour :
              </div>
              <div className="rental-modal-product-name">{productName}</div>
              <div className="rental-modal-date-info">
                <strong>{establishmentName}</strong>
              </div>
              <div className="rental-modal-date-info-mt">
                Cette action va passer le statut à "À livrer".
              </div>
            </div>
          </div>

          <div className="row">
            {/* Référence interne - TOUJOURS AFFICHE EN PREMIER */}
            <div className="col-12 mb-3">
              <label className="rental-modal-form-label rental-modal-form-label-required">
                Référence interne
              </label>
              <div className="rental-ref-interne-input-wrapper">
                <input
                  type="text"
                  value={validationRefInterne || ''}
                  onChange={(e) => onRefInterneChange(e.target.value, { action: "input-change" })}
                  onFocus={this.handleRefInterneFocus}
                  onBlur={this.handleRefInterneBlur}
                  disabled={isLoading}
                  placeholder="Saisir une référence interne..."
                  className="rental-ref-interne-input"
                />
                {validationRefInterne && (
                  <button
                    type="button"
                    className="rental-ref-interne-clear-btn"
                    onClick={() => onRefInterneSelect(null)}
                    disabled={isLoading}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
                <div className="rental-ref-interne-search-icon">
                  <i className="fas fa-search"></i>
                </div>
                {isSearchingEquipments && (
                  <div className="rental-ref-interne-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                )}
                {this.state.refInterneFocused && equipmentSearchResults.length > 0 && (
                  <div className="rental-ref-interne-suggestions">
                    {equipmentSearchResults.map((eq) => {
                      // L'équipement est bloqué seulement si l'établissement de l'équipement
                      // correspond à celui de la location active (cohérence des données)
                      const equipmentEstablishmentId = (eq.establishment_id || eq.data?.establishment_id)?.toString();
                      const activeRental = eq.active_rental || eq.data?.active_rental;
                      const rentalEstablishmentId = activeRental?.rental_establishment?._id?.toString();
                      const hasActiveRental = eq.has_active_rental || eq.data?.has_active_rental;
                      // Bloquer seulement si les établissements correspondent
                      const isBlocked = hasActiveRental && equipmentEstablishmentId && rentalEstablishmentId && equipmentEstablishmentId === rentalEstablishmentId;
                      return (
                        <div
                          key={eq._id || eq.value}
                          className={`rental-ref-interne-suggestion-item ${isBlocked ? 'suggestion-item-disabled' : ''}`}
                          onMouseDown={() => onRefInterneSelect(eq.data || eq)}
                        >
                          <span className="suggestion-ref">{eq.internal_ref || eq.label}</span>
                          <span className="suggestion-details">
                            {eq.establishment?.name || (eq.data?.establishment?.name) || "Libre"} - {eq.brand?.name || eq.data?.brand?.name || ""} {eq.model?.name || eq.data?.model?.name || ""}
                          </span>
                          {isBlocked && (
                            <span className="suggestion-warning">
                              <i className="fas fa-exclamation-triangle"></i> Location en cours
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {rental.ref_interne && validationRefInterne === rental.ref_interne && (
                <div className="alert alert-secondary mt-2" role="alert">
                  <i className="fas fa-user-tag"></i> Le client a demandé que l'équipement soit enregistré avec cette référence interne.
                </div>
              )}
              {scenario === "new" && (
                <div className="alert alert-info mt-2" role="alert">
                  <i className="fas fa-info-circle"></i> Cette référence
                  n'existe pas dans le parc. Un nouvel équipement sera créé.
                </div>
              )}
              {scenario === "existing-free" && !this.hasActiveRental() && (
                <div className="alert alert-success mt-2" role="alert">
                  <i className="fas fa-check-circle"></i> Équipement trouvé
                  dans le parc et disponible.
                </div>
              )}
              {selectedEquipment && this.hasActiveRental() && (
                <div className="alert alert-danger mt-2" role="alert">
                  <i className="fas fa-ban"></i>{" "}
                  <strong>Cet équipement a déjà une location en cours</strong>
                  {selectedEquipment.active_rental?.rental_establishment?.name && (
                    <span> chez <strong>{selectedEquipment.active_rental.rental_establishment.name}</strong></span>
                  )}
                  . Veuillez sélectionner un autre équipement ou créer une nouvelle référence.
                </div>
              )}
              {scenario === "existing-assigned" && !this.hasActiveRental() && (
                <div className="alert alert-warning mt-2" role="alert">
                  <i className="fas fa-exclamation-triangle"></i>{" "}
                  {warningMessage}
                </div>
              )}
            </div>

            {/* SCENARIO C: Champs supplémentaires si nouvel équipement */}
            {showExtraFields && (
              <>
                {/* Type field */}
                <div className="col-12 form-group">
                  <label className="rental-modal-form-label rental-modal-form-label-required">
                    Type
                  </label>
                  <AutocompleteInput
                    ref={this.typeInputRef}
                    options={materialsTypes || []}
                    selectedId={selectedMaterialType}
                    onChange={onMaterialTypeChange}
                    onCreateOption={onCreateMaterialType}
                    placeholder="Saisir le type"
                    disabled={isLoading}
                  />
                </div>

                {/* Brand field */}
                <div className="col-12 form-group">
                  <label className="rental-modal-form-label rental-modal-form-label-required">
                    Marque
                  </label>
                  <AutocompleteInput
                    ref={this.brandInputRef}
                    options={brands || []}
                    selectedId={selectedBrand}
                    onChange={(brandId) => {
                      onBrandChange(brandId);
                      // Reset model when brand changes
                      if (!brandId) {
                        onModelChange(null);
                      }
                      // Force re-render pour mettre à jour l'état du champ Modèle
                      this.forceUpdate();
                    }}
                    onInputChange={this.handleBrandInputChange}
                    onCreateOption={onCreateBrand}
                    placeholder="Saisir la marque"
                    disabled={isLoading}
                  />
                </div>

                {/* Model field */}
                <div className="col-12 form-group">
                  <label className="rental-modal-form-label rental-modal-form-label-required">
                    Modèle
                  </label>
                  <AutocompleteInput
                    ref={this.modelInputRef}
                    options={modelOptions}
                    selectedId={selectedModel}
                    onChange={onModelChange}
                    onCreateOption={onCreateModel}
                    placeholder="Saisir le modèle"
                    disabled={isLoading || !this.hasBrandValue()}
                  />
                </div>
              </>
            )}

            {/* Date de désinfection - uniquement pour équipement libre (non assigné) */}
            {scenario === "existing-free" && this.isEquipmentFree() && (
              <div className="col-12 mb-3">
                <label className="rental-modal-form-label rental-modal-form-label-required">
                  Date de désinfection
                </label>
                <input
                  type="date"
                  className="rental-modal-form-select"
                  value={this.props.disinfectionDate || ""}
                  onChange={(e) => this.props.onDisinfectionDateChange(e.target.value)}
                  disabled={isLoading}
                  max={new Date().toISOString().split("T")[0]}
                />
                <small className="text-muted">
                  Date de la dernière désinfection de l'équipement
                </small>
              </div>
            )}

            {/* État du dispositif - TOUJOURS AFFICHE */}
            <div className="col-12 mb-3">
              <label className="rental-modal-form-label rental-modal-form-label-required">
                État du dispositif
              </label>
              <select
                className="rental-modal-form-select"
                value={selectedEquipmentState}
                onChange={onEquipmentStateChange}
                disabled={isLoading}
              >
                <option value="">Sélectionner un état...</option>
                <option value={EqState.NEW}>Neuf</option>
                <option value={EqState.GOOD}>Bon</option>
                <option value={EqState.AVERAGE}>Moyen</option>
                <option value={EqState.BAD}>Mauvais</option>
              </select>
            </div>

            {/* SCENARIO B: Checkbox de confirmation pour réassignation (masqué si location active) */}
            {scenario === "existing-assigned" && !this.hasActiveRental() && (
              <div className="col-12 mb-2">
                <div className="rental-confirmation-checkbox">
                  <label
                    htmlFor="confirmReassignment"
                    className="rental-checkbox-label"
                  >
                    <input
                      type="checkbox"
                      id="confirmReassignment"
                      checked={confirmReassignment}
                      onChange={(e) =>
                        this.props.onConfirmReassignmentChange(e.target.checked)
                      }
                      disabled={isLoading}
                      className="rental-checkbox-input"
                    />
                    <span className="rental-checkbox-box">
                      <i className="fas fa-check rental-checkbox-icon"></i>
                    </span>
                    <span className="rental-checkbox-text">
                      Je confirme vouloir réassigner cet équipement à{" "}
                      <strong>{establishmentName}</strong>
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="rental-modal-footer">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="rental-modal-btn-cancel"
          >
            Annuler
          </button>
          <button
            onClick={this.handleConfirmWithAutoCreate}
            disabled={isLoading || !this.isFormValidWithPendingValues()}
            className={`rental-modal-btn-primary ${
              isLoading || !this.isFormValidWithPendingValues()
                ? "rental-modal-btn-primary-disabled"
                : "rental-modal-btn-primary-enabled"
            }`}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span>Validation...</span>
              </>
            ) : (
              <>Valider la location</>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default RentalValidationModal;
